import { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import TopBar from './components/TopBar';
import ToastContainer from './components/ToastContainer';
import ShopPage from './pages/ShopPage';
import CategoryPage from './pages/CategoryPage';
import SignUpPage from './pages/SignUpPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import WishlistPage from './pages/WishlistPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import SearchPage from './pages/SearchPage';
import EditProfilePage from './pages/EditProfilePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import AdminPage from './pages/AdminPage';
import AddressesPage from './pages/AddressesPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import LegalPage from './pages/LegalPage';
import FAQPage from './pages/FAQPage';
import DeleteProfilePage from './pages/DeleteProfilePage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import { INITIAL_PRODUCTS } from './data/products';
import { fetchProducts, fetchBanners, fetchHeroSlides, fetchTheme, fetchCurrentUser, fetchCategories, logout, fetchChatMessages, sendChatMessage, fetchWishlist, toggleWishlist, fetchOrders } from './db';
import { toast } from './toast';

export const INITIAL_HERO_SLIDES = [];
export const INITIAL_BANNERS = [];

const DEFAULT_THEME = { name: 'Manna Bridal Default', bg: '#FAF3F0', surface: '#FFFFFF', primary: '#F47B20', text: '#1A1A2E' };

export default function App() {
  const getInitialPage = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      if (u && u.role && u.role !== 'customer') return 'admin';
    } catch (_) { }
    return 'shop';
  };

  const [page, setPage] = useState(getInitialPage());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFetching, setIsFetching] = useState(true); // inline content loading, never blocks UI

  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [banners, setBanners] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  // Track if a manual refreshData() call has happened — if so, init() should not overwrite with stale data
  const hasManualRefresh = useRef(false);
  const initFetchedAt = useRef(0);
  const [categories, setCategories] = useState([]);

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch (_) { return null; }
  });

  // ── Hide HTML preloader once first data fetch is done ───────────────────
  useEffect(() => {
    if (!isFetching) {
      const el = document.getElementById('mb-preloader');
      if (el) {
        el.classList.add('hide');
        setTimeout(() => { el.style.display = 'none'; }, 380);
      }
    }
  }, [isFetching]);

  useEffect(() => {
    if (user && user.role && user.role !== 'customer') {
      if (page !== 'admin') {
        setPage('admin');
      }
    }
  }, [user, page]);

  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedUser = JSON.parse(localStorage.getItem('user'));
      if (savedUser?.id) {
        const saved = localStorage.getItem(`mb_cart_${savedUser.id}`);
        if (saved) return JSON.parse(saved);
      }
    } catch (e) { }
    return [];
  });

  // ── Wishlist ──────────────────────────────────────────────────────────────
  const [wishlistIds, setWishlistIds] = useState([]);

  useEffect(() => {
    if (user) {
      fetchWishlist().then(data => {
        setWishlistIds(data.map(w => w.product_id));
      }).catch(err => console.error('Failed to fetch wishlist', err));
    } else {
      setWishlistIds([]);
    }
  }, [user]);

  // ── Orders Notifications ───────────────────────────────────────────────────
  const [unreadOrderCount, setUnreadOrderCount] = useState(0);
  const [unpaidOrders, setUnpaidOrders] = useState([]);

  useEffect(() => {
    if (!user || user.role !== 'customer') return;
    const loadOrders = async () => {
      try {
        const orders = await fetchOrders();
        const lastViewed = localStorage.getItem('mb_orders_last_viewed') || 0;
        const newUpdates = orders.filter(o => 
          o.payment_status !== 'unpaid' &&
          new Date(o.updated_at).getTime() > parseInt(lastViewed, 10)
        );
        setUnreadOrderCount(newUpdates.length);
        // Unpaid orders = pending payment
        setUnpaidOrders(orders.filter(o => o.payment_status === 'unpaid' && o.status !== 'cancelled' && o.status !== 'failed'));
      } catch (err) {}
    };
    loadOrders();
    const interval = setInterval(loadOrders, 8000);
    return () => clearInterval(interval);
  }, [user]);

  const handleToggleWishlist = async (productId) => {
    if (!user) {
      toast.error('Please log in to use the wishlist.');
      setPage('login');
      return;
    }
    // Optimistic UI update
    setWishlistIds(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
    try {
      const res = await toggleWishlist(productId);
      if (res.status === 'added' && !wishlistIds.includes(productId)) {
        toast.success('Added to wishlist');
      } else if (res.status === 'removed' && wishlistIds.includes(productId)) {
        toast.success('Removed from wishlist');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update wishlist');
      // Revert if failed
      fetchWishlist().then(data => setWishlistIds(data.map(w => w.product_id))).catch(()=>{});
    }
  };

  // ── Chat ──────────────────────────────────────────────────────────────────
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [adminLastSeen, setAdminLastSeen] = useState(null);  // null = online
  const chatBottomRef = useRef(null);

  // ── Bot reply engine ─────────────────────────────────────────────────────
  const BOT_REPLIES = [
    { pattern: /order|track|where.*order|order.*status/i,  reply: "To track your order, go to **My Orders** from the navigation menu and you'll see real-time status updates. You can also confirm delivery directly from there once your package arrives! 📦" },
    { pattern: /payment|pay|checkout|failed|transaction/i,  reply: "For payment issues, please ensure your card details are correct and you have sufficient funds. If the problem persists, try refreshing the page and attempting checkout again. Still stuck? We're here to help! 💳" },
    { pattern: /return|refund|exchange|cancel/i,            reply: "We accept returns within 7 days of delivery for unworn items with original tags. To initiate a return or refund, please reply here with your order reference number and we'll process it promptly. ✨" },
    { pattern: /shipping|delivery|deliver|how long|when/i, reply: "We deliver nationwide! Standard delivery takes **24–48 hours** after payment confirmation. You can also choose **Store Pickup** at checkout for free. We'll send you a tracking update once your item ships! 🚚" },
    { pattern: /size|fit|measurement|chart/i,              reply: "We have a full size guide available on each product page — look for the **SIZE GUIDE** button. If you're between sizes, we recommend going a size up for bridal gowns. Need specific measurements? Share the item name and we'll advise! 📏" },
    { pattern: /price|discount|promo|coupon|sale|offer/i,  reply: "We regularly run promotions! 🎉 Keep an eye on our homepage banners for the latest offers. For bulk orders or special occasions, reach out directly and we'll see what we can do for you!" },
    { pattern: /whatsapp|call|phone|contact|speak|human/i, reply: "You can reach our sales team directly on WhatsApp at **+234 800 000 0000** (Mon–Sat, 9am–6pm). We'd love to chat! 💬" },
    { pattern: /stock|available|sold out|out of stock/i,   reply: "Stock availability is shown on each product page. If an item shows **'Sold Out'**, you can reach out here and we'll notify you as soon as it's restocked! 🛍️" },
    { pattern: /hello|hi |hey |good morning|good afternoon|good evening/i, reply: "Hello! 👋 Welcome to Manna Bridal. How can we assist you today? Feel free to ask about orders, shipping, sizing, or anything else!" },
    { pattern: /thank|thanks|appreciate/i,                reply: "You're very welcome! 😊 Is there anything else we can help you with today?" },
  ];

  const getBotReply = (message) => {
    for (const { pattern, reply } of BOT_REPLIES) {
      if (pattern.test(message)) return reply;
    }
    return "Thanks for reaching out! 🌸 A member of our team will respond shortly. In the meantime, feel free to browse our collection or check our FAQ for quick answers.";
  };

  // Removed scroll lock to allow native scrolling and prevent website jumping

  // ── Load data from Laravel API on mount (NON-BLOCKING — UI is always interactive) ─────
  useEffect(() => {
    const init = async () => {
      try {
        const [p, b, h, t, cats] = await Promise.all([
          fetchProducts().catch(() => []),
          fetchBanners().catch(() => []),
          fetchHeroSlides().catch(() => []),
          fetchTheme().catch(() => null),
          fetchCategories().catch(() => []),
        ]);
        const fetchedAt = Date.now();
        initFetchedAt.current = fetchedAt;
        if (Array.isArray(p) && p.length > 0) {
          setProducts(p);
          
          // Check for /product/ID in URL for sharing
          const path = window.location.pathname;
          if (path.startsWith('/product/')) {
            const parts = path.split('/');
            const productIdParam = parts[parts.length - 1];
            if (productIdParam) {
              const found = p.find(prod => String(prod.id) === productIdParam);
              if (found) {
                setSelectedProduct(found);
                setPage('productdetail');
              }
            }
          }
        }
        if (Array.isArray(b)) setBanners(b);
        // Only update heroSlides if no manual refresh has happened more recently
        if (Array.isArray(h) && !hasManualRefresh.current) setHeroSlides(h);
        if (t && t.primary) setTheme(t);
        if (Array.isArray(cats)) setCategories(cats);

        // Refresh logged-in user token silently
        const hasToken = !!localStorage.getItem('auth_token');
        if (hasToken) {
          try {
            const freshUser = await fetchCurrentUser();
            if (freshUser) {
              setUser(freshUser);
              localStorage.setItem('user', JSON.stringify(freshUser));
            }
          } catch (_) {
            // Do NOT wipe localStorage here. 
            // The 401 interceptor in db.js will handle true session expirations.
            // If it's a network timeout, we just keep the cached user.
          }
        }
      } catch (err) {
        console.error('Background fetch failed:', err);
      } finally {
        setIsFetching(false);
      }
    };
    init();
    const interval = setInterval(() => {
      // Refresh products every 5 minutes (backend caches for 1 hour, but refresh on admin changes)
      fetchProducts().then(p => { if (Array.isArray(p)) setProducts(p); }).catch(()=>{});
    }, 300000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply theme CSS variables globally ────────────────────────────────────
  useEffect(() => {
    document.documentElement.style.setProperty('--primary', theme.primary);
    document.documentElement.style.setProperty('--bg', theme.bg);
    document.documentElement.style.setProperty('--surface', theme.surface || '#FFFFFF');
    document.documentElement.style.setProperty('--text', theme.text);

    const hexToRgb = (hex) => {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '244, 123, 32';
    };

    const darkenColor = (hex, percent) => {
      try {
        const num = parseInt(hex.replace('#', ''), 16),
          amt = Math.round(2.55 * percent),
          R = ((num >> 16) & 0xFF) - amt,
          G = ((num >> 8) & 0xFF) - amt,
          B = (num & 0xFF) - amt;
        return '#' + (0x1000000 + (R < 0 ? 0 : R > 255 ? 255 : R) * 0x10000 + (G < 0 ? 0 : G > 255 ? 255 : G) * 0x100 + (B < 0 ? 0 : B > 255 ? 255 : B)).toString(16).slice(1);
      } catch (e) { return '#D96A10'; }
    };

    document.documentElement.style.setProperty('--primary-rgb', hexToRgb(theme.primary));
    document.documentElement.style.setProperty('--primary-dark', darkenColor(theme.primary, 10));
  }, [theme]);

  // ── Persist cart locally (scoped to user) ─────────────────────────────
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`mb_cart_${user.id}`, JSON.stringify(cartItems));
    }
  }, [cartItems, user]);

  // Load correct cart on login/logout
  useEffect(() => {
    if (user?.id) {
      try {
        const saved = localStorage.getItem(`mb_cart_${user.id}`);
        setCartItems(saved ? JSON.parse(saved) : []);
      } catch (e) {
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
  }, [user?.id]);

  const refreshData = async () => {
    hasManualRefresh.current = true;
    try {
      const [p, b, h, t, c] = await Promise.all([
        fetchProducts().catch(err => { console.error('refresh fetchProducts failed:', err); return []; }),
        fetchBanners().catch(err => { console.error('refresh fetchBanners failed:', err); return []; }),
        fetchHeroSlides().catch(err => { console.error('refresh fetchHeroSlides failed:', err); return []; }),
        fetchTheme().catch(err => { console.error('refresh fetchTheme failed:', err); return null; }),
        fetchCategories().catch(err => { console.error('refresh fetchCategories failed:', err); return []; }),
      ]);
      if (Array.isArray(p)) setProducts(p);
      if (Array.isArray(b)) setBanners(b);
      if (Array.isArray(h)) setHeroSlides(h);
      if (t && t.primary) setTheme(t);
      if (Array.isArray(c)) setCategories(c);
    } catch (error) {
      console.error('Failed to refresh data', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    setPage('shop');
  };

  // ── Auto-logout when server returns 401 (expired/invalidated token) ──────
  useEffect(() => {
    const onAuthExpired = () => {
      setUser(null);
      setPage('login');
      toast.error('Your session has expired. Please log in again.');
    };
    window.addEventListener('auth-expired', onAuthExpired);
    return () => window.removeEventListener('auth-expired', onAuthExpired);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Navigation ────────────────────────────────────────────────────────────
  const [lastOrder, setLastOrder] = useState(null);
  // ── History API for Mobile Back Navigation ──
  useEffect(() => {
    window.history.replaceState({ page: getInitialPage(), data: null }, '');
    const handlePopState = (event) => {
      const st = event.state;
      if (st) {
        setPage(st.page || getInitialPage());
        if (st.data?.product) setSelectedProduct(st.data.product);
        if (st.data?.category) setSelectedCategory(st.data.category);
        if (st.data?.query) setSearchQuery(st.data.query);
      } else {
        setPage(getInitialPage());
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = (newPage, data, replace = false) => {
    const authPages = ['cart', 'checkout', 'wishlist', 'orders', 'account', 'editprofile', 'addresses', 'deleteprofile'];
    if (!user && authPages.includes(newPage)) {
      setPage('login');
      if (!replace) window.history.pushState({ page: 'login', data: null }, '');
      return;
    }
    if (user && user.role && user.role !== 'customer' && newPage !== 'admin') {
      newPage = 'admin';
    }
    
    if (!replace) {
      window.history.pushState({ page: newPage, data }, '');
    }

    if (newPage === 'orders') {
      localStorage.setItem('mb_orders_last_viewed', Date.now().toString());
      setUnreadOrderCount(0);
    }
    
    setPage(newPage);
    if (data?.product) setSelectedProduct(data.product);
    if (data?.category) setSelectedCategory(data.category);
    if (data?.newOrder) setLastOrder(data.newOrder);
    if (data?.query) setSearchQuery(data.query);
    if (newPage === 'orderconfirmation') setCartItems([]);
    
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (product, selectedSize = null, selectedColor = null) => {
    if (!user) {
      setPage('login');
      return;
    }
    const productToAdd = { ...product };
    if (selectedSize) productToAdd.size = selectedSize;
    if (selectedColor) productToAdd.color = selectedColor;
    const itemId = `${productToAdd.id}${productToAdd.size ? '-' + productToAdd.size : ''}${productToAdd.color ? '-' + productToAdd.color : ''}`;
    const currentQty = cartItems.filter(i => `${i.id}${i.size ? '-' + i.size : ''}${i.color ? '-' + i.color : ''}` === itemId).length;
    
    if (product.stock !== undefined && currentQty + 1 > product.stock) {
      toast.error(`Only ${product.stock} unit(s) available.`);
      return;
    }
    
    setCartItems([...cartItems, productToAdd]);
    toast.success('Added to cart');
  };
  const getCartItemId = (item) => `${item.id}${item.size ? '-' + item.size : ''}${item.color ? '-' + item.color : ''}`;
  const handleRemoveFromCart = (productId, size = null, color = null) => {
    const idToRemove = `${productId}${size ? '-' + size : ''}${color ? '-' + color : ''}`;
    setCartItems(cartItems.filter(i => getCartItemId(i) !== idToRemove));
  };
  const handleUpdateCartQuantity = (productId, newQuantity, size = null, color = null) => {
    if (newQuantity <= 0) { handleRemoveFromCart(productId, size, color); return; }
    const idToUpdate = `${productId}${size ? '-' + size : ''}${color ? '-' + color : ''}`;
    const item = cartItems.find(i => getCartItemId(i) === idToUpdate);
    if (!item) return;
    
    if (item.stock !== undefined && newQuantity > item.stock) {
      toast.error(`Only ${item.stock} unit(s) available.`);
      newQuantity = item.stock;
    }
    
    setCartItems([...cartItems.filter(i => getCartItemId(i) !== idToUpdate), ...Array(newQuantity).fill(item)]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const msgText = chatInput;
    const now = new Date().toISOString();
    // Do not use local: true for user messages so they get overwritten safely by backend
    setChatMessages(prev => [...prev, { is_admin_reply: false, message: msgText, created_at: now }]);
    setChatInput('');
    setIsTyping(true);
    setAdminLastSeen(null); // show as "typing" in header

    try {
      await sendChatMessage(msgText);
    } catch (err) {
      console.error('Failed to send chat message', err);
    }

    // Simulate bot "thinking" delay (1.5–2.5s) then reply
    const delay = 1500 + Math.random() * 1000;
    setTimeout(async () => {
      const botReply = getBotReply(msgText);
      const replyTime = new Date().toISOString();
      const newBotMsg = { is_admin_reply: true, message: botReply, created_at: replyTime, local_bot: true };
      
      setChatMessages(prev => [...prev, newBotMsg]);
      setIsTyping(false);
      // Keep admin online by NOT setting last seen here

      // Also sync with real server messages in background
      try {
        const msgs = await fetchChatMessages();
        if (msgs.length > 0) {
          setChatMessages(prev => {
            const botMsgs = prev.filter(m => m.local_bot);
            return [...msgs, ...botMsgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          });
        }
      } catch (_) {}
    }, delay);
  };

  const [unreadChatCount, setUnreadChatCount] = useState(0);

  useEffect(() => {
    if (isChatOpen) setUnreadChatCount(0);
  }, [isChatOpen]);

  // Poll for new messages (constantly running to support notifications)
  useEffect(() => {
    if (!user) return;
    
    const fetchMsgs = async () => {
      try {
        const msgs = await fetchChatMessages();
        if (msgs.length > 0) {
          setChatMessages(prev => {
            const botMsgs = prev.filter(m => m.local_bot);
            const allMsgs = [...msgs, ...botMsgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            
            setUnreadChatCount(currentUnread => {
              if (isChatOpen) return 0;
              const prevAdminMsgCount = prev.filter(m => m.is_admin_reply && !m.local_bot).length;
              const newAdminMsgCount = msgs.filter(m => m.is_admin_reply).length;
              if (newAdminMsgCount > prevAdminMsgCount) {
                return currentUnread + (newAdminMsgCount - prevAdminMsgCount);
              }
              return currentUnread;
            });
            
            const adminReplies = allMsgs.filter(m => m.is_admin_reply);
            if (adminReplies.length > 0) {
              const lastAdminMsg = adminReplies[adminReplies.length - 1];
              const lastSeenDate = new Date(lastAdminMsg.created_at);
              if (Date.now() - lastSeenDate.getTime() > 5 * 60 * 1000) {
                setAdminLastSeen(lastSeenDate);
              } else {
                setAdminLastSeen(null);
              }
            }
            return allMsgs;
          });
        }
      } catch (err) {
        console.error('Failed to fetch chat messages', err);
      }
    };
    fetchMsgs(); // Initial fetch
    const interval = setInterval(() => fetchMsgs(), 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [user, isChatOpen]);

  // Auto-scroll chat to bottom on new messages or typing indicator
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isTyping]);

  // ── Force password change gate (blocks ALL pages) ────────────────────────
  if (user && user.must_change_password) {
    return (
      <ForceChangePasswordPage
        user={user}
        onPasswordChanged={(updatedUser) => {
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          // Now route to their correct page
          if (updatedUser.role && updatedUser.role !== 'customer') {
            setPage('admin');
          } else {
            setPage('shop');
          }
        }}
      />
    );
  }

  // ── Admin page (same React tree — changes propagate instantly to shop) ────
  if (page === 'admin') {
    return (
      <AdminPage
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        products={products}
        banners={banners}
        heroSlides={heroSlides}
        categories={categories}
        theme={theme}
        refreshData={refreshData}
        user={user}
      />
    );
  }

  const isFullPage = ['signup', 'login', 'productdetail', 'checkout', 'orderconfirmation'].includes(page);
  const showBottomNav = !isFullPage;

  const renderPage = () => {
    switch (page) {
      case 'shop': return <ShopPage onNavigate={handleNavigate} products={products} banners={banners} heroSlides={heroSlides} theme={theme} categories={categories} isFetching={isFetching} wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} onAddToCart={handleAddToCart} />;
      case 'categorydetail': return <CategoryPage onNavigate={handleNavigate} category={selectedCategory} products={products} wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} />;
      case 'search': return <SearchPage query={searchQuery} products={products} onNavigate={handleNavigate} onToggleWishlist={handleToggleWishlist} wishlistIds={wishlistIds} />;
      case 'productdetail': return <ProductDetailPage product={selectedProduct} products={products} onNavigate={handleNavigate} onAddToCart={handleAddToCart} onAddToWishlist={handleToggleWishlist} isWished={wishlistIds.includes(selectedProduct?.id)} theme={theme} />;
      case 'cart': return <CartPage cartItems={cartItems} onNavigate={handleNavigate} onUpdateQuantity={handleUpdateCartQuantity} onRemoveItem={handleRemoveFromCart} />;
      case 'checkout': return <CheckoutPage cartItems={cartItems} onNavigate={handleNavigate} user={user} />;
      case 'orderconfirmation': return <OrderConfirmationPage onNavigate={handleNavigate} order={lastOrder} />;
      case 'orders': return <OrdersPage onNavigate={handleNavigate} />;
      case 'wishlist': return <WishlistPage wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} products={products} onNavigate={handleNavigate} />;
      case 'account': return <AccountPage onNavigate={handleNavigate} onOpenChat={() => setIsChatOpen(true)} user={user} onLogout={handleLogout} categories={categories} unpaidOrders={unpaidOrders} />;
      case 'editprofile': return <EditProfilePage onNavigate={handleNavigate} user={user} onUserUpdate={setUser} />;
      case 'addresses': return <AddressesPage onNavigate={handleNavigate} user={user} />;
      case 'legal': return <LegalPage onNavigate={handleNavigate} />;
      case 'faq': return <FAQPage onNavigate={handleNavigate} />;
      case 'deleteprofile': return <DeleteProfilePage onNavigate={handleNavigate} />;
      case 'signup': return <SignUpPage onNavigate={handleNavigate} onUserLogin={setUser} />;
      case 'login': return <LoginPage onNavigate={handleNavigate} onUserLogin={setUser} />;
      default: return <ShopPage onNavigate={handleNavigate} products={products} banners={banners} heroSlides={heroSlides} theme={theme} categories={categories} isFetching={isFetching} wishlistIds={wishlistIds} onToggleWishlist={handleToggleWishlist} onAddToCart={handleAddToCart} />;
    }
  };


  return (
    <div className="min-h-screen relative" style={{ backgroundColor: theme.bg, color: theme.text }}>
      <ToastContainer />
      {!isFullPage && <Sidebar />}
      {!isFullPage && <TopBar activePage={page} onNavigate={handleNavigate} categories={categories} cartCount={cartItems.length} wishlistCount={wishlistIds.length} products={products} unreadOrderCount={unreadOrderCount} />}

      <main>
        {renderPage()}
      </main>

      {showBottomNav && <BottomNav activePage={page} onNavigate={handleNavigate} badges={{
        orders: unreadOrderCount,
        account: unpaidOrders.length,
        cart: cartItems.length,
        wishlist: wishlistIds.length > 0 ? 0 : 0,
      }} />}

      {/* Floating Chat Bubble */}
      {user && !isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed z-40 bg-[#F47B20] text-white p-4 rounded-full shadow-2xl hover:scale-105 hover:bg-[#D96A10] transition-all flex items-center justify-center border border-white/10"
          style={{ bottom: showBottomNav ? '72px' : '24px', right: '20px' }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {unreadChatCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-lg animate-bounce">
              {unreadChatCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {user && isChatOpen && (
        <div
          className="fixed z-50 bg-white rounded-3xl w-[calc(100%-40px)] sm:w-96 h-[520px] max-h-[80vh] shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
          style={{ bottom: showBottomNav ? '80px' : '24px', right: '20px' }}
        >
          {/* ── Header ── */}
          <div className="bg-[#1A1A2E] text-white px-4 py-3 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Avatar with pulsing online ring */}
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F47B20] to-[#D96A10] flex items-center justify-center font-black text-xs shadow-lg">MB</div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 border-2 border-[#1A1A2E] rounded-full"
                  style={{ boxShadow: '0 0 0 2px #22c55e, 0 0 6px 2px rgba(34,197,94,0.5)' }}
                />
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">Manna Bridal Support</p>
                <p className="text-[10px] font-medium leading-tight">
                  {isTyping
                    ? <span className="text-green-400 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="inline-block w-1 h-1 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="inline-block w-1 h-1 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        <span className="ml-0.5">typing...</span>
                      </span>
                    : adminLastSeen
                      ? <span className="text-gray-400">last seen {new Date(adminLastSeen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      : <span className="text-green-400">● Online</span>
                  }
                </p>
              </div>
            </div>
            <button onClick={() => setIsChatOpen(false)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10 text-gray-300 hover:text-white transition-all text-sm">
              ✕
            </button>
          </div>

          {/* ── Messages ── */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-[#FAF3F0]" style={{ scrollbarWidth: 'thin' }}>
            {/* Greeting */}
            <div className="flex flex-col items-start">
              <div className="flex items-end gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#F47B20] to-[#D96A10] flex items-center justify-center text-white text-[8px] font-black flex-shrink-0">MB</div>
                <div className="max-w-[78%] rounded-2xl rounded-tl-none px-4 py-2.5 text-xs shadow-sm leading-relaxed bg-white text-[#1A1A2E]">
                  👋 Hello! Welcome to Manna Bridal. How can we help you today?
                </div>
              </div>
              <span className="text-[9px] text-gray-400 mt-1 pl-8">Just now</span>
            </div>

            {chatMessages.map((msg, i) => {
              const isUser = !msg.is_admin_reply;
              const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
              // Parse **bold** markdown
              const renderText = (text) => {
                const parts = text.split(/(\*\*[^*]+\*\*)/g);
                return parts.map((part, idx) =>
                  part.startsWith('**') && part.endsWith('**')
                    ? <strong key={idx}>{part.slice(2, -2)}</strong>
                    : part
                );
              };
              return (
                <div key={i} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
                  <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                    {!isUser && (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#F47B20] to-[#D96A10] flex items-center justify-center text-white text-[8px] font-black flex-shrink-0">MB</div>
                    )}
                    <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-xs shadow-sm leading-relaxed whitespace-pre-wrap ${
                      isUser ? 'bg-[#F47B20] text-white rounded-tr-none' : 'bg-white text-[#1A1A2E] rounded-tl-none'
                    }`}>
                      {(() => {
                        const text = msg.message || '';
                        if (text.includes('Attached Image: http')) {
                          const [main, imgUrl] = text.split('Attached Image: ');
                          return (<>
                            {renderText(main)}
                            <div className="mt-2">
                              <a href={imgUrl} target="_blank" rel="noreferrer">
                                <img src={imgUrl} alt="Attached" className="max-w-[150px] rounded-lg border border-white/20" />
                              </a>
                            </div>
                          </>);
                        }
                        return renderText(text);
                      })()}
                    </div>
                  </div>
                  <span className={`text-[9px] text-gray-400 mt-1 ${isUser ? 'pr-1' : 'pl-8'}`}>
                    {timeStr}{isUser ? ' ✓✓' : ''}
                  </span>
                </div>
              );
            })}

            {/* Typing indicator bubble */}
            {isTyping && (
              <div className="flex flex-col items-start">
                <div className="flex items-end gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#F47B20] to-[#D96A10] flex items-center justify-center text-white text-[8px] font-black flex-shrink-0">MB</div>
                  <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-[#F47B20] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#F47B20] rounded-full animate-bounce" style={{ animationDelay: '180ms' }} />
                    <span className="w-2 h-2 bg-[#F47B20] rounded-full animate-bounce" style={{ animationDelay: '360ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={chatBottomRef} />
          </div>

          {/* ── Quick Suggestions ── */}
          <div className="px-3 pt-2 flex gap-2 overflow-x-auto pb-0 flex-shrink-0" style={{ scrollbarWidth: 'none' }}>
            {['Where is my order?', 'Shipping info', 'Size guide', 'Return policy'].map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setChatInput(suggestion)}
                className="whitespace-nowrap bg-[#FEF0E6] hover:bg-[#F47B20] hover:text-white text-[#F47B20] border border-[#F47B20]/30 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-all flex-shrink-0"
              >
                {suggestion}
              </button>
            ))}
          </div>

          {/* ── Input ── */}
          <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
            <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
              <textarea
                rows="2"
                placeholder="Ask about prices, shipping, orders..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-2xl text-xs outline-none focus:border-[#F47B20] resize-none transition-colors bg-gray-50"
              />
              <button
                type="submit"
                disabled={!chatInput.trim()}
                className="w-10 h-10 flex-shrink-0 bg-[#F47B20] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-[#D96A10] text-white rounded-2xl flex items-center justify-center transition-all active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
